import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom';
import { MdCached } from 'react-icons/md';
import { GiPartyPopper } from 'react-icons/gi';
import { TbMessageQuestion } from "react-icons/tb";
import { MdOutlineDashboard } from 'react-icons/md';
import { TbReceipt } from 'react-icons/tb';
import { useContextState } from '../../context/Context';

const ApplicationBar = () => {
    const location = useLocation();
    const { inboxCTX } = useContextState();
    const { inboxState } = inboxCTX;

    const unreadCount = useMemo(() => {
        const list = inboxState.supportUserInboxState ?? [];
        return list.reduce((sum: number, inbox: any) => sum + (inbox.count ?? 0), 0);
    }, [inboxState.supportUserInboxState]);

    // Exact-match only — startsWith('/')  would make Events always active.
    const isActive = (paths: string[]) =>
        paths.some(p => location.pathname === p);

    const activeClass = 'text-violet-600';
    const inactiveClass = 'text-gray-500 group-hover:text-blue-600';

    const navClass = (paths: string[]) =>
        `inline-flex flex-col items-center justify-center px-3 hover:bg-gray-50 group ${
            isActive(paths) ? 'bg-gray-50' : ''
        }`;

    const iconClass = (paths: string[]) =>
        `w-5 h-5 mb-1 ${isActive(paths) ? activeClass : inactiveClass}`;

    const labelClass = (paths: string[]) =>
        `text-[10px] font-medium ${isActive(paths) ? 'text-violet-600' : 'text-gray-700 group-hover:text-gray-900'}`;

    return (
        <div className="fixed z-50 w-full h-16 max-w-2xl -translate-x-1/2 bg-white border border-gray-200 rounded-full bottom-4 left-1/2 shadow-sm">
            <div className="grid h-full max-w-2xl grid-cols-7 mx-auto">

                <Link
                    to="/dashboard"
                    className={`${navClass(['/dashboard'])} rounded-l-full`}
                >
                    <MdOutlineDashboard className={iconClass(['/dashboard'])} />
                    <span className={labelClass(['/dashboard'])}>Dash</span>
                </Link>

                <Link to="/events" className={navClass(['/', '/events', '/event'])}>
                    <GiPartyPopper className={iconClass(['/', '/events', '/event'])} />
                    <span className={labelClass(['/', '/events', '/event'])}>Events</span>
                </Link>

                <Link to="/tickets" className={navClass(['/tickets'])}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className={`bi bi-ticket-detailed mb-1 ${isActive(['/tickets']) ? activeClass : inactiveClass}`}
                        viewBox="0 0 16 16"
                    >
                        <path d="M4 5.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5Zm0 5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5ZM5 7a1 1 0 0 0 0 2h6a1 1 0 1 0 0-2H5Z"/>
                        <path d="M0 4.5A1.5 1.5 0 0 1 1.5 3h13A1.5 1.5 0 0 1 16 4.5V6a.5.5 0 0 1-.5.5 1.5 1.5 0 0 0 0 3 .5.5 0 0 1 .5.5v1.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 11.5V10a.5.5 0 0 1 .5-.5 1.5 1.5 0 1 0 0-3A.5.5 0 0 1 0 6V4.5ZM1.5 4a.5.5 0 0 0-.5.5v1.05a2.5 2.5 0 0 1 0 4.9v1.05a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-1.05a2.5 2.5 0 0 1 0-4.9V4.5a.5.5 0 0 0-.5-.5h-13Z"/>
                    </svg>
                    <span className={labelClass(['/tickets'])}>Tickets</span>
                </Link>

                <Link to="/users" className={navClass(['/users'])}>
                    <svg
                        className={`w-5 h-5 mb-1 ${isActive(['/users']) ? activeClass : inactiveClass}`}
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path d="M10 0a10 10 0 1 0 10 10A10.011 10.011 0 0 0 10 0Zm0 5a3 3 0 1 1 0 6 3 3 0 0 1 0-6Zm0 13a8.949 8.949 0 0 1-4.951-1.488A3.987 3.987 0 0 1 9 13h2a3.987 3.987 0 0 1 3.951 3.512A8.949 8.949 0 0 1 10 18Z"/>
                    </svg>
                    <span className={labelClass(['/users'])}>Users</span>
                </Link>

                <Link to="/transactions" className={navClass(['/transactions'])}>
                    <TbReceipt className={iconClass(['/transactions'])} />
                    <span className={labelClass(['/transactions'])}>Tx</span>
                </Link>

                <Link to="/cache" className={navClass(['/cache'])}>
                    <MdCached className={iconClass(['/cache'])} />
                    <span className={labelClass(['/cache'])}>Cache</span>
                </Link>

                <Link
                    to="/inbox"
                    className={`${navClass(['/inbox', '/messages'])} rounded-r-full`}
                >
                    <div className="relative">
                        <TbMessageQuestion className={iconClass(['/inbox', '/messages'])} />
                        {unreadCount > 0 && (
                            <span className="absolute -top-1 -right-2 inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </span>
                        )}
                    </div>
                    <span className={labelClass(['/inbox', '/messages'])}>Inbox</span>
                </Link>

            </div>
        </div>
    );
};

export default ApplicationBar;
